import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  Building2, 
  Briefcase, 
  Users, 
  CreditCard, 
  Server, 
  Activity, 
  Shield, 
  LogOut, 
  Package, 
  Settings, 
  Cpu, 
  Code, 
  CircleDot,
  ChevronDown,
  Layers,
  Inbox,
  PieChart,
  MessageSquare,
  BarChart,
  Megaphone,
  CreditCard as IntegrationsIcon,
  HelpCircle
} from 'lucide-react';
import { View } from '../../../types.ts';
import { useAuth } from '../../../context/AuthContext.tsx';
import { useEvents } from '../../../context/EventContext.tsx';
import EventDrawer from '../../../components/layout/EventDrawer.tsx';
import { useBranding } from '../../../hooks/useBranding.ts';

interface AdminSidebarProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  currentView: View | string;
  setCurrentView: (view: View) => void;
}

export default function AdminSidebar({ isCollapsed, toggleSidebar, currentView, setCurrentView }: AdminSidebarProps) {
  const { logout, user } = useAuth();
  const { iconUrl, appName } = useBranding();
  const { getPulseState, getBadgeCount } = useEvents();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<'deploys' | 'users' | 'billing' | 'infrastructure' | 'logs' | 'all'>('all');

  const onOpenEvents = (category: typeof selectedCategory) => {
    setSelectedCategory(category);
    setDrawerOpen(true);
  };

  const categories = [
    {
      title: 'Main Menu',
      items: [
        { id: 'admin', icon: LayoutDashboard, label: 'Dashboard' },
        { id: 'admin-tenants', icon: Layers, label: 'Products' },
        { id: 'admin-billing', icon: Inbox, label: 'Transactions' },
        { id: 'admin-finance', icon: PieChart, label: 'Reports & Analytics' },
      ]
    },
    {
      title: 'Customers',
      items: [
        { id: 'admin-users', icon: Users, label: 'Customer List' },
        { id: 'admin-companies', icon: Briefcase, label: 'Channels' },
      ]
    },
    {
      title: 'Management',
      items: [
        { id: 'admin-settings', icon: Shield, label: 'Roles & Permissions' },
        { id: 'admin-plans', icon: IntegrationsIcon, label: 'Billing & Subscription' },
        { id: 'admin-bes', icon: BarChart, label: 'BES Management' },
        { id: 'admin-integrations', icon: Code, label: 'Integrations' },
      ]
    },
    {
      title: 'Intelligence',
      items: [
        { id: 'admin-ai-control-center', icon: Cpu, label: 'AI Control Center' },
      ]
    },
    {
      title: 'Settings',
      items: [
        { id: 'admin-infrastructure', icon: Settings, label: 'System Settings' },
      ]
    }
  ];

  return (
    <aside className={`hidden md:flex bg-[#0E0E10] text-[#6B6B6F] flex-col transition-all duration-300 relative z-30 h-[100dvh] shrink-0 ${isCollapsed ? 'w-[72px]' : 'w-[240px]'}`}>
      <div className={`flex items-center h-[80px] shrink-0 ${isCollapsed ? 'justify-center' : 'px-6'} py-5`}>
        <div className={`w-10 h-10 rounded-[12px] ${iconUrl ? 'bg-white' : 'bg-white text-[#0E0E10]'} flex items-center justify-center font-[700] tracking-tight text-[18px] shrink-0 overflow-hidden`}>
          {iconUrl ? <img src={iconUrl} alt={appName} className="w-full h-full object-contain p-1" /> : appName.charAt(0).toUpperCase()}
        </div>
        {!isCollapsed && (
          <div className="ml-3 flex flex-col min-w-0">
            <span className="font-bold text-xs tracking-tight text-white leading-none">{appName} Admin</span>
            <span className="text-[10px] text-[#A1A1AA] font-medium leading-none mt-1">Platform Team</span>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 space-y-6 scrollbar-thin scrollbar-thumb-[#2A2A2E] mt-2">
        {categories.map((category) => (
          <div key={category.title} className="space-y-1 w-full flex flex-col items-center">
            {!isCollapsed && (
              <h3 className="px-4 w-full text-[10px] font-semibold text-[#5A5A5E] select-none mb-2 tracking-wider uppercase">
                {category.title}
              </h3>
            )}
            <div className={`space-y-1 ${isCollapsed ? 'w-full flex flex-col items-center' : 'w-full px-2'}`}>
              {category.items.map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id;
                
                if (isCollapsed) {
                  return (
                    <button
                      key={item.id}
                      onClick={() => setCurrentView(item.id as View)}
                      title={item.label}
                      className={`w-10 h-10 rounded-[12px] flex items-center justify-center transition-colors ${
                        isActive ? 'bg-white text-black' : 'text-[#6B6B6F] hover:bg-[#1A1A1E] hover:text-white'
                      }`}
                    >
                      <Icon size={18} strokeWidth={1.7} />
                    </button>
                  );
                }

                return (
                  <button
                    key={item.id}
                    onClick={() => setCurrentView(item.id as View)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[12px] transition-all group relative ${
                      isActive 
                        ? 'bg-white text-[#0F0F10] font-semibold shadow-sm' 
                        : 'text-[#8A8D93] hover:text-white hover:bg-[#1A1A1E]'
                    }`}
                  >
                    <Icon size={16} strokeWidth={isActive ? 2 : 1.7} className="shrink-0" />
                    <div className="flex items-center justify-between flex-1 min-w-0">
                      <span className="text-[13px] tracking-tight whitespace-nowrap truncate">{item.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-4 shrink-0 flex flex-col items-center gap-3 mb-2">
        <div className={`flex items-center gap-3 ${isCollapsed ? 'justify-center' : 'w-full px-2 py-2 hover:bg-[#1A1A1E] rounded-xl transition-colors cursor-pointer'}`}>
          <div className="w-8 h-8 rounded-full bg-[#1E1E20] border border-[#2A2A2E] overflow-hidden shrink-0">
            <img 
              src={user?.photoURL || "https://i.pravatar.cc/100?img=33"} 
              alt="Admin avatar" 
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
            />
          </div>
          {!isCollapsed && (
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-[11px] font-semibold text-white truncate leading-none mb-1">{user?.displayName || 'Admin User'}</span>
              <span className="text-[9px] text-[#8A8D93] tracking-wide truncate">Platform Admin</span>
            </div>
          )}
        </div>
      </div>

      <EventDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        category={selectedCategory}
        onNavigateToView={(view) => setCurrentView(view as any)}
      />
    </aside>
  );
}
